/**
 * LandingPage — citizen-facing front door, ported from
 * `screens/src/pages/LandingPage.jsx`. Layout is preserved verbatim; behaviour
 * differences vs the screens mockup:
 *
 *   - The "Track a Package" link uses the existing TrackingLookupForm route
 *     surface (`/track`) — wired by App.tsx in this app to land on a page
 *     that mounts the form. The screens design uses `/track` as a route too.
 *   - i18n is deferred per the slice brief; literal English is hard-coded.
 *
 * Static content blocks (services, capabilities, trust badges, stats) ship
 * as inline data — they're marketing copy, not a backend concern.
 */
import { type JSX, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { env } from '../../lib/env';
import {
  Mail,
  Truck,
  Banknote,
  Building2,
  Landmark,
  Receipt,
  Search,
  UserCircle2,
  KeyRound,
  ShieldCheck,
  Database,
  Lock,
  Eye,
  Users,
  GitBranch,
  ChevronRight,
  Quote,
  Sparkles,
  MoveRight,
  Wallet,
  Globe,
  CreditCard,
  ArrowRight,
  MapPin,
  Menu,
  X,
  type LucideIcon,
} from 'lucide-react';

/* ─────────────────── Scroll-reveal hook ─────────────────── */
function useReveal(): [React.RefObject<HTMLDivElement | null>, boolean] {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState<boolean>(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e?.isIntersecting) {
          setVisible(true);
          obs.unobserve(el);
        }
      },
      { threshold: 0.15 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

interface RevealProps {
  readonly children: ReactNode;
  readonly className?: string;
  readonly delay?: number;
}

function Reveal({ children, className = '', delay = 0 }: RevealProps): JSX.Element {
  const [ref, visible] = useReveal();
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

/* ─────────────────── Audience paths ─────────────────── */
interface AudienceLink {
  readonly label: string;
  readonly to: string;
  readonly icon: LucideIcon;
  /** When true, renders an `<a target="_blank" rel="noopener noreferrer">`
   *  instead of a react-router `<Link>`. Used for staff-portal handoff. */
  readonly external?: boolean;
}

interface Audience {
  readonly key: string;
  readonly label: string;
  readonly title: string;
  readonly desc: string;
  readonly primary: AudienceLink;
  readonly secondary: AudienceLink;
  readonly icon: LucideIcon;
  readonly accent: string;
  /** Optional override for primary-button text color. Defaults to white;
   *  set to a near-black for light accents (e.g. NIPOST yellow #FDD700) to
   *  keep the label readable. */
  readonly primaryTextColor?: string;
}

const audiences: readonly Audience[] = [
  {
    key: 'public',
    label: 'For the public',
    title: 'Track. Send. Find an outlet.',
    desc: 'Track any parcel by its number, locate the nearest NIPOST outlet, or learn about the new services you can get at any branch.',
    primary: { label: 'Track a Package', to: '/track', icon: Search },
    secondary: { label: 'Find an Outlet', to: '/portal/outlets', icon: MapPin },
    icon: Globe,
    accent: '#27245f',
  },
  {
    key: 'customer',
    label: 'For customers',
    title: 'Your NIPOST account.',
    desc: 'Send parcels from anywhere, top up your wallet, pay bills, and access the full range of NIPOST and government services through one account.',
    primary: { label: 'Customer Sign In', to: '/auth/login', icon: UserCircle2 },
    secondary: { label: 'Create Account', to: '/auth/register', icon: ChevronRight },
    icon: Wallet,
    accent: '#FDD700',
    primaryTextColor: '#111827',  // gray-900 — yellow needs near-black for legibility
  },
  {
    key: 'staff',
    label: 'For NIPOST staff & franchise operators',
    title: 'Operations system.',
    desc: 'The platform that runs the counter, the back office, the franchise network, the finance ledger and every outlet, in real time.',
    primary: { label: 'Staff Sign In', to: `${env.webStaffBaseUrl}/auth/login`, icon: KeyRound, external: true },
    secondary: { label: 'Help & Support', to: '/help', icon: ChevronRight },
    icon: Building2,
    accent: '#0F766E',
  },
];

/* ─────────────────── NIPOST core services ─────────────────── */
interface CitizenService {
  readonly name: string;
  readonly desc: string;
  readonly icon: LucideIcon;
  readonly color: string;
}

const citizenServices: readonly CitizenService[] = [
  { name: 'Mail & parcels', desc: 'Standard nationwide delivery, plus EMS Speedpost for express domestic and international shipping.', icon: Mail, color: '#27245f' },
  { name: 'E-commerce & logistics', desc: 'Tailored support for SMEs — warehousing, fulfilment and last-mile delivery on the largest network in Nigeria.', icon: Truck, color: '#0369A1' },
  { name: 'Financial services', desc: "Domestic and international money transfers, bills, airtime and data through NIPOST's dedicated financial services unit.", icon: Banknote, color: '#FDD700' },
  { name: 'Retail solutions', desc: 'Stamps, secure PO Boxes, and Private Mail Bags (PMB) — your trusted address with NIPOST.', icon: Receipt, color: '#7C3AED' },
  { name: 'Government services', desc: "NIN, passport, driver's licence, tax and more — accessible at any NIPOST outlet.", icon: Landmark, color: '#1D4ED8' },
  { name: 'Industry regulation', desc: 'NIPOST issues licences to and regulates private courier and logistics operators in Nigeria.', icon: ShieldCheck, color: '#DC2626' },
];

/* ─────────────────── Operations capabilities ─────────────────── */
interface OperationsCapability {
  readonly name: string;
  readonly desc: string;
  readonly icon: LucideIcon;
}

const operationsCapabilities: readonly OperationsCapability[] = [
  { name: 'Counter & back office', desc: 'Mail intake, payment, sorting, dispatch, end-of-day reconciliation.', icon: Mail },
  { name: 'Franchise & hierarchy', desc: 'Tier 1/2/3 onboarding, multi-outlet groups, automated revenue share.', icon: GitBranch },
  { name: 'Real-time finance', desc: 'Daily remittance, ledger, recipient accounts, gateway reconciliation.', icon: CreditCard },
  { name: 'Identity & compliance', desc: 'KYC, NIN/BVN, sanctions screening, NDPR, CBN-aligned controls.', icon: ShieldCheck },
];

/* ─────────────────── Trust badges ─────────────────── */
interface TrustBadge {
  readonly label: string;
  readonly icon: LucideIcon;
}

const trustBadges: readonly TrustBadge[] = [
  { label: 'NDPR Compliant', icon: ShieldCheck },
  { label: 'CBN Regulated', icon: Lock },
  { label: 'AES-256 Encryption', icon: Database },
  { label: 'Immutable Audit', icon: Eye },
  { label: 'Sanctions Screening', icon: Users },
  { label: 'Hosted in Nigeria', icon: Globe },
];

/* ─────────────────── Stats ─────────────────── */
const stats: readonly { readonly value: string; readonly label: string }[] = [
  { value: '3,000+', label: 'Post Offices' },
  { value: '774', label: 'LGAs Covered' },
  { value: '36+1', label: 'States & FCT' },
  { value: '6', label: 'Service Lines' },
];

/* ─────────────────── Component ─────────────────── */
export function LandingPage(): JSX.Element {
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);

  const navLinks: readonly { readonly href: string; readonly label: string }[] = [
    { href: '#audiences', label: 'Get Started' },
    { href: '#pmg', label: 'From the PMG' },
    { href: '#services', label: 'Services' },
    { href: '#operations', label: 'Operations' },
  ];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'DM Sans', 'Inter', system-ui, sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;1,9..40,400&family=Playfair+Display:wght@700;800;900&display=swap" rel="stylesheet" />

      {/* ━━━ NAV ━━━ */}
      <nav className="fixed top-0 inset-x-0 z-50 bg-white/85 backdrop-blur-xl border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" onClick={() => setMobileMenuOpen(false)}>
            <span
              aria-hidden="true"
              className="flex h-9 w-9 items-center justify-center rounded-md bg-nipost-green text-sm font-bold text-white"
            >
              NP
            </span>
            <div className="hidden sm:block leading-none border-l border-gray-200 pl-3">
              <span className="block text-[11px] tracking-[0.2em] uppercase text-gray-400 font-medium">Nigerian Postal Service</span>
              <span className="block text-sm font-bold text-gray-900 mt-0.5">The new NIPOST</span>
            </div>
          </Link>

          <div className="hidden md:flex items-center gap-8 text-sm font-medium text-gray-600">
            {navLinks.map((l) => (
              <a key={l.href} href={l.href} className="hover:text-nipost-green transition-colors">
                {l.label}
              </a>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <Link to="/track" className="text-sm font-medium text-gray-700 hover:text-nipost-green transition-colors">
              Track a Package
            </Link>
            <Link
              to="/auth/login"
              className="bg-nipost-gold text-gray-900 text-sm font-semibold px-5 py-2 rounded-lg hover:brightness-95 transition-all shadow-md shadow-nipost-gold/30"
            >
              Customer Sign In
            </Link>
          </div>

          <button
            type="button"
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileMenuOpen}
            onClick={() => setMobileMenuOpen((v) => !v)}
            className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {mobileMenuOpen && (
          <>
            <div
              className="md:hidden fixed inset-0 top-16 bg-black/30 backdrop-blur-sm z-40"
              onClick={() => setMobileMenuOpen(false)}
              aria-hidden="true"
            />
            <div className="md:hidden absolute top-16 inset-x-0 bg-white border-b border-gray-100 shadow-lg z-50">
              <div className="max-w-7xl mx-auto px-6 py-4 space-y-1">
                {navLinks.map((l) => (
                  <a
                    key={l.href}
                    href={l.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className="block px-3 py-3 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    {l.label}
                  </a>
                ))}
                <div className="pt-3 mt-3 border-t border-gray-100 space-y-2">
                  <Link
                    to="/track"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center justify-between px-3 py-3 rounded-lg text-sm font-semibold text-gray-700 border border-gray-200 hover:border-nipost-green hover:text-nipost-green"
                  >
                    <span className="inline-flex items-center gap-2">
                      <Search className="w-4 h-4" /> Track a Package
                    </span>
                    <ChevronRight className="w-4 h-4" />
                  </Link>
                  <Link
                    to="/auth/login"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center justify-between px-3 py-3 rounded-lg text-sm font-semibold text-gray-900 bg-nipost-gold hover:brightness-95"
                  >
                    <span className="inline-flex items-center gap-2">
                      <UserCircle2 className="w-4 h-4" /> Customer Sign In
                    </span>
                    <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          </>
        )}
      </nav>

      {/* ━━━ HERO ━━━ */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-0 right-0 w-[800px] h-[800px] rounded-full opacity-[0.04]" style={{ background: 'radial-gradient(circle, #27245f 0%, transparent 70%)' }} />
          <div className="absolute bottom-0 left-0 w-[600px] h-[600px] rounded-full opacity-[0.04]" style={{ background: 'radial-gradient(circle, #FDD700 0%, transparent 70%)' }} />
        </div>

        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-4xl">
            <Reveal>
              <div className="inline-flex items-center gap-2 bg-nipost-green/5 border border-nipost-green/10 rounded-full px-4 py-1.5 mb-8">
                <Sparkles className="w-3.5 h-3.5 text-nipost-green" />
                <span className="text-sm font-medium text-nipost-green">A new chapter for Nigeria&apos;s postal service</span>
              </div>
            </Reveal>

            <Reveal delay={100}>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-gray-900 leading-[1.05] tracking-tight mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                Welcome to the{' '}
                <span className="relative whitespace-nowrap">
                  <span className="text-nipost-green">new NIPOST</span>
                </span>
              </h1>
            </Reveal>

            <Reveal delay={200}>
              <p className="text-xl text-gray-500 leading-relaxed max-w-2xl mb-10">
                More than 3,000 post offices, reaching every local government area in Nigeria — modernised for parcels, e-commerce logistics, financial services, retail and government services. Whether you&apos;re a citizen, a customer, or part of the NIPOST team, this is your starting point.
              </p>
            </Reveal>

            <Reveal delay={300}>
              <div className="flex flex-wrap gap-3">
                <a href="#audiences" className="group inline-flex items-center gap-2 bg-nipost-green text-white font-semibold px-7 py-3.5 rounded-xl hover:bg-nipost-green-dark transition-all shadow-lg shadow-nipost-green/25">
                  Choose where you&apos;re going
                  <MoveRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>
                <Link to="/track" className="inline-flex items-center gap-2 bg-white text-gray-700 font-semibold px-7 py-3.5 rounded-xl border-2 border-gray-200 hover:border-nipost-green hover:text-nipost-green transition-all">
                  <Search className="w-4 h-4" />
                  Track a Package
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ━━━ AUDIENCE TRIAGE ━━━ */}
      <section id="audiences" className="py-16 bg-gradient-to-b from-white via-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <div className="text-center mb-12">
              <span className="text-sm font-semibold text-nipost-gold uppercase tracking-widest">Who&apos;s visiting?</span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mt-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                Pick the door that fits you
              </h2>
              <p className="text-gray-500 mt-3 max-w-xl mx-auto">Three paths, one network. Citizens and customers find their service in seconds. NIPOST staff and franchise operators sign in to the operations system.</p>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-5">
            {audiences.map((a, i) => {
              const PrimaryIcon = a.primary.icon;
              const SecondaryIcon = a.secondary.icon;
              const AudienceIcon = a.icon;
              return (
                <Reveal key={a.key} delay={i * 100}>
                  <div className="relative group bg-white rounded-2xl p-7 border border-gray-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 h-full flex flex-col">
                    <div className="h-1.5 -mt-7 -mx-7 mb-6 rounded-t-2xl" style={{ backgroundColor: a.accent }} />
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${a.accent}15` }}>
                        <AudienceIcon className="w-5 h-5" style={{ color: a.accent }} />
                      </div>
                      <span className="text-[11px] font-bold uppercase tracking-widest" style={{ color: a.accent }}>{a.label}</span>
                    </div>
                    <h3 className="text-2xl font-extrabold text-gray-900 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>{a.title}</h3>
                    <p className="text-sm text-gray-500 leading-relaxed mb-6 flex-1">{a.desc}</p>
                    <div className="flex flex-col gap-2">
                      {a.primary.external ? (
                        <a
                          href={a.primary.to}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`group/btn inline-flex items-center justify-between gap-2 px-5 py-3 rounded-xl font-semibold text-sm transition-all hover:opacity-90 ${
                            a.primaryTextColor ? 'text-gray-900' : 'text-white'
                          }`}
                          style={{ backgroundColor: a.accent }}
                        >
                          <span className="inline-flex items-center gap-2">
                            <PrimaryIcon className="w-4 h-4" />
                            {a.primary.label}
                          </span>
                          <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                        </a>
                      ) : (
                        <Link
                          to={a.primary.to}
                          className={`group/btn inline-flex items-center justify-between gap-2 px-5 py-3 rounded-xl font-semibold text-sm transition-all hover:opacity-90 ${
                            a.primaryTextColor ? 'text-gray-900' : 'text-white'
                          }`}
                          style={{ backgroundColor: a.accent }}
                        >
                          <span className="inline-flex items-center gap-2">
                            <PrimaryIcon className="w-4 h-4" />
                            {a.primary.label}
                          </span>
                          <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                        </Link>
                      )}
                      {a.secondary.external ? (
                        <a
                          href={a.secondary.to}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-between gap-2 px-5 py-2.5 rounded-xl text-gray-700 font-medium text-sm border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-all"
                        >
                          <span className="inline-flex items-center gap-2">
                            <SecondaryIcon className="w-4 h-4" />
                            {a.secondary.label}
                          </span>
                          <ChevronRight className="w-4 h-4" />
                        </a>
                      ) : (
                        <Link
                          to={a.secondary.to}
                          className="inline-flex items-center justify-between gap-2 px-5 py-2.5 rounded-xl text-gray-700 font-medium text-sm border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-all"
                        >
                          <span className="inline-flex items-center gap-2">
                            <SecondaryIcon className="w-4 h-4" />
                            {a.secondary.label}
                          </span>
                          <ChevronRight className="w-4 h-4" />
                        </Link>
                      )}
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ━━━ PMG WELCOME ━━━ */}
      <section id="pmg" className="py-24 bg-nipost-green-dark text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '28px 28px' }} />
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full opacity-10" style={{ background: 'radial-gradient(circle, #FDD700 0%, transparent 70%)' }} />

        <div className="max-w-6xl mx-auto px-6 relative">
          <Reveal>
            <div className="text-center mb-12">
              <span className="text-sm font-semibold text-nipost-gold uppercase tracking-widest">A message from</span>
              <h2 className="text-4xl sm:text-5xl font-extrabold mt-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                Tola Odeyemi
              </h2>
              <p className="text-white/70 mt-2 text-sm uppercase tracking-widest">Postmaster General · Nigerian Postal Service</p>
            </div>
          </Reveal>

          <Reveal delay={200}>
            <div className="grid md:grid-cols-3 gap-10 items-center">
              <div className="md:col-span-1 flex justify-center md:justify-end">
                <div className="relative">
                  <div className="w-48 h-48 sm:w-56 sm:h-56 rounded-2xl bg-gradient-to-br from-white/15 to-white/5 border border-white/20 backdrop-blur-sm overflow-hidden shadow-2xl shadow-black/40 ring-2 ring-nipost-gold/30 flex items-center justify-center">
                    <span className="text-7xl font-bold text-white/40" style={{ fontFamily: "'Playfair Display', serif" }}>TO</span>
                  </div>
                  <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-nipost-gold text-nipost-green-dark text-[10px] font-extrabold uppercase tracking-widest px-3 py-1 rounded-full whitespace-nowrap shadow-lg">
                    Postmaster General
                  </div>
                </div>
              </div>

              <div className="md:col-span-2">
                <Quote className="w-12 h-12 text-nipost-gold mb-5" />
                <blockquote className="text-lg sm:text-xl leading-relaxed text-white/90 font-light mb-6" style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}>
                  &ldquo;For more than half a century, the Nigerian Postal Service has been the country&apos;s most extensive public network — over 3,000 post offices, present in every state and every local government area. That reach is our inheritance, and our responsibility.
                  <br /><br />
                  We are now reintroducing that network to Nigeria as the modern service it has always promised to be. Faster mail and EMS Speedpost delivery. E-commerce logistics built for SMEs. Financial services — domestic and international transfers, retail payments, agency banking. Stamps, PO Boxes and Private Mail Bags brought online. Government services accessible at every counter. And, as the regulator of this industry, a clean, transparent framework for the private operators we license.
                  <br /><br />
                  Every transaction now sits on a single auditable platform. Every post office, every franchise operator, every last-mile point works inside the same system as headquarters. The result is a postal service that is dependable, that competes, and that delivers — for the citizen sending a package, the entrepreneur paying a vendor, and the family verifying a relative&apos;s identity. This is the NIPOST you can trust again.&rdquo;
                </blockquote>
                <div className="flex items-center gap-3">
                  <div className="h-px flex-1 bg-white/20" />
                  <div className="text-right">
                    <p className="font-bold text-white">Tola Odeyemi</p>
                    <p className="text-xs text-white/60 uppercase tracking-widest mt-0.5">Postmaster General, NIPOST</p>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ━━━ STATS ━━━ */}
      <section className="bg-white border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((s, i) => (
              <Reveal key={s.label} delay={i * 80}>
                <div className="text-center">
                  <div className="text-4xl sm:text-5xl font-extrabold text-nipost-green leading-none" style={{ fontFamily: "'Playfair Display', serif" }}>{s.value}</div>
                  <div className="text-xs text-gray-500 font-medium uppercase tracking-widest mt-2">{s.label}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ━━━ SERVICES ━━━ */}
      <section id="services" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <div className="text-center mb-14">
              <span className="text-sm font-semibold text-nipost-gold uppercase tracking-widest">What NIPOST does</span>
              <h2 className="text-4xl sm:text-5xl font-extrabold text-gray-900 mt-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                One network. Six core services.
              </h2>
              <p className="text-gray-500 mt-4 max-w-2xl mx-auto">Mail and parcels, e-commerce logistics, financial services, retail solutions, government services, and industry regulation — delivered through 3,000+ post offices reaching every local government area in Nigeria.</p>
            </div>
          </Reveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {citizenServices.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal key={s.name} delay={i * 80}>
                  <div className="bg-white rounded-2xl border border-gray-100 p-6 hover:border-nipost-green/20 hover:shadow-lg transition-all duration-300 h-full">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: `${s.color}12` }}>
                      <Icon className="w-5 h-5" style={{ color: s.color }} />
                    </div>
                    <h4 className="font-bold text-gray-900 text-base mb-1.5">{s.name}</h4>
                    <p className="text-sm text-gray-500 leading-relaxed">{s.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>

          <Reveal delay={200}>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-3 text-sm">
              <Link to="/auth/register" className="inline-flex items-center gap-2 bg-nipost-green text-white font-semibold px-6 py-3 rounded-xl hover:bg-nipost-green-dark transition-colors shadow-md shadow-nipost-green/20">
                Create your NIPOST account <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/portal/outlets" className="inline-flex items-center gap-2 bg-white text-gray-700 font-medium px-6 py-3 rounded-xl border border-gray-200 hover:border-gray-300 transition-colors">
                Find an outlet near you <MapPin className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ━━━ OPERATIONS ━━━ */}
      <section id="operations" className="py-24">
        <div className="max-w-5xl mx-auto px-6">
          <Reveal>
            <div className="text-center mb-12">
              <span className="text-sm font-semibold text-nipost-gold uppercase tracking-widest">For NIPOST teams</span>
              <h2 className="text-4xl sm:text-5xl font-extrabold text-gray-900 mt-3 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                The operations system that runs the network
              </h2>
              <p className="text-gray-500 max-w-2xl mx-auto">From the counter clerk in Maiduguri to the Postmaster General&apos;s office in Abuja — everyone works inside the same platform. Real-time visibility, system-enforced rules, and a single source of truth for every transaction.</p>
            </div>
          </Reveal>

          <div className="grid sm:grid-cols-2 gap-4 mb-10">
            {operationsCapabilities.map((c, i) => {
              const Icon = c.icon;
              return (
                <Reveal key={c.name} delay={i * 80}>
                  <div className="bg-white rounded-2xl border border-gray-100 hover:border-nipost-green/20 hover:shadow-lg transition-all duration-300 p-6 flex items-start gap-4 h-full">
                    <div className="w-11 h-11 rounded-xl bg-nipost-green/8 flex items-center justify-center shrink-0">
                      <Icon className="w-5 h-5 text-nipost-green" />
                    </div>
                    <div>
                      <p className="font-bold text-gray-900 text-base mb-1">{c.name}</p>
                      <p className="text-sm text-gray-500 leading-relaxed">{c.desc}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>

          <Reveal delay={200}>
            <div className="flex flex-wrap justify-center gap-3">
              <a
                href={`${env.webStaffBaseUrl}/auth/login`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-nipost-green text-white font-semibold px-6 py-3 rounded-xl hover:bg-nipost-green-dark transition-colors shadow-lg shadow-nipost-green/20"
              >
                <KeyRound className="w-4 h-4" /> Sign in to Operations
              </a>
              <Link to="/help" className="inline-flex items-center gap-2 bg-white text-gray-700 font-medium px-6 py-3 rounded-xl border-2 border-gray-200 hover:border-nipost-green hover:text-nipost-green transition-colors">
                Help &amp; support <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ━━━ TRUST ━━━ */}
      <section className="py-16 bg-gray-50 border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <div className="text-center mb-8">
              <span className="text-sm font-semibold text-nipost-gold uppercase tracking-widest">Built to be trusted</span>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mt-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                Government-grade. Citizen-friendly.
              </h2>
            </div>
          </Reveal>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {trustBadges.map((t, i) => {
              const Icon = t.icon;
              return (
                <Reveal key={t.label} delay={i * 50}>
                  <div className="bg-white rounded-xl border border-gray-100 px-4 py-3 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-nipost-green/8 flex items-center justify-center shrink-0">
                      <Icon className="w-4 h-4 text-nipost-green" />
                    </div>
                    <span className="text-xs font-semibold text-gray-700">{t.label}</span>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ━━━ CTA ━━━ */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Reveal>
            <h2 className="text-4xl sm:text-5xl font-extrabold text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>
              The post office, reborn.
            </h2>
            <p className="text-gray-500 mt-5 text-lg max-w-xl mx-auto">
              Whether you came to track a package, open an account, or sign in to run the network — you&apos;re in the right place.
            </p>
            <div className="flex flex-wrap justify-center gap-3 mt-9">
              <Link to="/track" className="inline-flex items-center gap-2 bg-nipost-green text-white font-semibold px-7 py-3.5 rounded-xl hover:bg-nipost-green-dark transition-colors shadow-lg shadow-nipost-green/25">
                <Search className="w-4 h-4" /> Track a Package
              </Link>
              <Link to="/auth/login" className="inline-flex items-center gap-2 bg-white text-gray-700 font-semibold px-7 py-3.5 rounded-xl border-2 border-gray-200 hover:border-nipost-green hover:text-nipost-green transition-colors">
                <UserCircle2 className="w-4 h-4" /> Customer Sign In
              </Link>
              <a
                href={`${env.webStaffBaseUrl}/auth/login`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-gray-900 text-white font-semibold px-7 py-3.5 rounded-xl hover:bg-gray-800 transition-colors"
              >
                <KeyRound className="w-4 h-4" /> Staff &amp; Operators
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ━━━ FOOTER ━━━ */}
      <footer className="bg-gray-900 text-white py-14">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-10 mb-10">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <span
                  aria-hidden="true"
                  className="flex h-10 w-10 items-center justify-center rounded-md bg-white text-sm font-bold text-nipost-green"
                >
                  NP
                </span>
                <div>
                  <p className="font-bold text-base">Nigerian Postal Service</p>
                  <p className="text-xs text-gray-500">Unified Postal Management Platform</p>
                </div>
              </div>
              <p className="text-sm text-gray-400 leading-relaxed max-w-sm">
                A trusted national network for parcel delivery, financial services, government services and digital identity — connecting every Nigerian to the public infrastructure they need.
              </p>
            </div>
            <div>
              <p className="font-semibold text-sm mb-4 text-gray-300">For citizens</p>
              <div className="space-y-2 text-sm text-gray-500">
                <Link to="/track" className="block hover:text-white transition-colors">Track a Package</Link>
                <Link to="/portal/outlets" className="block hover:text-white transition-colors">Find an Outlet</Link>
                <Link to="/auth/register" className="block hover:text-white transition-colors">Create an Account</Link>
                <Link to="/auth/login" className="block hover:text-white transition-colors">Customer Sign In</Link>
              </div>
            </div>
            <div>
              <p className="font-semibold text-sm mb-4 text-gray-300">For staff &amp; operators</p>
              <div className="space-y-2 text-sm text-gray-500">
                <a
                  href={`${env.webStaffBaseUrl}/auth/login`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block hover:text-white transition-colors"
                >
                  Operations Sign In
                </a>
                <Link to="/help" className="block hover:text-white transition-colors">Help Centre</Link>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-6 flex flex-col sm:flex-row justify-between items-center gap-3">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
              <Link to="/legal/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <span className="text-gray-700">·</span>
              <Link to="/legal/terms" className="hover:text-white transition-colors">Terms &amp; Conditions</Link>
              <span className="text-gray-700">·</span>
              <span>Platform partner <span className="font-bold text-nipost-gold">INITS Limited</span></span>
            </div>
            <p className="text-xs text-gray-600">
              &copy; {new Date().getFullYear()} Nigerian Postal Service. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
